import { Sparkles, Play, PhoneCall } from "lucide-react";
import { SkeletonRows, EmptyState } from "./ClientDialerUi";

export function CallbacksPanel({ rows, today, loading }) {
  if (loading) return <SkeletonRows testid="cd-callbacks-loading" />;
  if (!rows.length) return <EmptyState testid="cd-callbacks">No open callbacks.</EmptyState>;
  return (
    <div data-testid="cd-callbacks" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((r) => {
        const due = r.due_date <= today;
        return (
          <div key={r.id} data-testid={`cd-callback-${r.id}`} style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee", borderLeft: `4px solid ${due ? "#BA7517" : "#2563eb"}`, padding: "12px 18px", display: "flex", alignItems: "center", gap: 14, fontSize: 12 }}>
            <PhoneCall style={{ width: 14, height: 14, color: due ? "#BA7517" : "#2563eb" }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}><a href={`/ehi-clients/${r.client_id}`} style={{ color: "inherit" }}>{r.clinic}</a> <span style={{ color: "#94a3b8", fontWeight: 400 }}>#{r.client_id}</span></div>
              <div style={{ fontSize: 11, color: "#888" }}>{r.next_action} · {r.rep_name}{r.notes ? ` · ${r.notes}` : ""}</div>
            </div>
            <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 600, background: due ? "#FFF3CD" : "#E6F1FB", color: due ? "#856404" : "#185FA5" }}>{due ? "Due" : "Upcoming"} · {r.due_date} {r.due_time}</span>
          </div>
        );
      })}
    </div>
  );
}

export function DncPanel({ rows, onLift, loading }) {
  if (loading) return <SkeletonRows testid="cd-dnc-loading" />;
  if (!rows.length) return <EmptyState testid="cd-dnc-list">No clients are on Do Not Call.</EmptyState>;
  return (
    <div data-testid="cd-dnc-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((r) => (
        <div key={r.client_id} data-testid={`cd-dnc-row-${r.client_id}`} style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee", borderLeft: "4px solid #A32D2D", padding: "12px 18px", display: "flex", alignItems: "center", gap: 14, fontSize: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}><a href={`/ehi-clients/${r.client_id}`} style={{ color: "inherit" }}>{r.clinic || `Client ${r.client_id}`}</a> <span style={{ color: "#94a3b8", fontWeight: 400 }}>#{r.client_id}</span></div>
            <div style={{ fontSize: 11, color: "#888" }}>Flagged by {r.by_name} · {r.at ? new Date(r.at).toLocaleDateString() : ""}{r.reason ? ` · ${r.reason}` : ""}</div>
          </div>
          <button data-testid={`cd-dnc-lift-${r.client_id}`} className="btn" style={{ fontSize: 11 }} onClick={() => onLift(r)}>Lift</button>
        </div>
      ))}
    </div>
  );
}

/** Agent view — Morning briefing + client campaigns, Leads queue style. */
export function ClientAgentView({ campaigns, callbacks, today, loading, onStart }) {
  const active = campaigns.filter((c) => c.status === "active");
  const queued = active.reduce((n, c) => n + Math.max(0, (c.client_count || 0) - (c.called_count || 0)), 0);
  return (
    <div data-testid="cd-agent-view">
      <div data-testid="cd-morning-briefing" style={{ background: "#0F2942", borderRadius: 10, padding: "14px 18px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <Sparkles style={{ width: 12, height: 12, color: "#93C5FD" }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: "#fff" }}>Morning briefing</span>
          <span style={{ fontSize: 10, color: "#8BA5C0", marginLeft: "auto" }}>AI-prioritized</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#34c759", marginTop: 5, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: "#CBD5E0" }}><strong data-testid="cd-briefing-count" style={{ color: "#fff" }}>{queued} {queued === 1 ? "client" : "clients"}</strong> in your queue{callbacks.length ? ` · ${callbacks.length} open callback${callbacks.length === 1 ? "" : "s"}` : ""}</span>
        </div>
      </div>
      {loading && <SkeletonRows testid="cd-agent-loading" />}
      {!loading && active.length === 0 && <EmptyState testid="cd-agent-empty">No client campaigns are assigned to you yet.</EmptyState>}
      {!loading && active.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
          <div className="eyebrow">Client campaigns</div>
          {active.map((c) => {
            const left = Math.max(0, (c.client_count || 0) - (c.called_count || 0));
            return (
              <div key={c.id} data-testid={`cd-agent-campaign-${c.id}`} style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee", borderLeft: "4px solid #1D9E75", padding: "12px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: "#888" }}>{left} {left === 1 ? "client" : "clients"} left · {c.called_count || 0} called</div>
                </div>
                <button data-testid={`cd-agent-start-${c.id}`} className="btn btn-primary" style={{ fontSize: 11, display: "inline-flex", gap: 4, alignItems: "center" }} onClick={() => onStart(c)}><Play style={{ width: 11, height: 11 }} /> Start calling</button>
              </div>
            );
          })}
        </div>
      )}
      {!loading && callbacks.length > 0 && (<>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Open callbacks</div>
        <CallbacksPanel rows={callbacks} today={today} />
      </>)}
    </div>
  );
}
